import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, AlertTriangle, Megaphone, Rocket, Tag, Settings2, ChevronRight } from 'lucide-react';
import { getActiveNotification } from '../api/Notifications.api';

// Un ítem del panel. Los avisos (ej. "Falta de logo") los arma el dashboard;
// las novedades vienen del banner activo de /api/notifications/active.
export interface BellItem {
  key: string;
  kind: 'aviso' | 'novedad';
  title: string;
  description?: string;
  icon?: string;
  actionText?: string;
  onClick?: () => void;
}

interface Props {
  customerId?: string;
  notices: BellItem[];
  onOpenPreferences?: () => void;
  className?: string;
}

const NovedadIcon: Record<string, React.ElementType> = { promo: Tag, cohete: Rocket, anuncio: Megaphone };

// Leídos por cliente en este navegador (comodidad por usuario, no es dato crítico)
const readKey = (customerId?: string) => `lisual:notif-leidas:${customerId || 'sin-cliente'}`;
const loadRead = (customerId?: string): string[] => {
  try { return JSON.parse(localStorage.getItem(readKey(customerId)) || '[]'); } catch { return []; }
};
const saveRead = (customerId: string | undefined, keys: string[]) => {
  try { localStorage.setItem(readKey(customerId), JSON.stringify(keys.slice(-200))); } catch { /* sin storage */ }
};

const NotificationBell: React.FC<Props> = ({ customerId, notices, onOpenPreferences, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [novedades, setNovedades] = useState<BellItem[]>([]);
  const [read, setRead] = useState<string[]>(() => loadRead(customerId));
  const [newOnOpen, setNewOnOpen] = useState<string[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setRead(loadRead(customerId)); }, [customerId]);

  useEffect(() => {
    let alive = true;
    getActiveNotification()
      .then((res) => {
        const a = Array.isArray(res) ? res[0] : res;
        if (!alive || !a || !a.isActive) return;
        setNovedades([{
          key: `novedad-${a.id || a._id || a.title}`,
          kind: 'novedad',
          title: a.title,
          description: a.description,
          icon: a.type,
          actionText: a.buttonIsActive ? a.buttonText : undefined,
          onClick: a.buttonIsActive && a.buttonLink
            ? () => window.open(/^https?:\/\//.test(a.buttonLink) ? a.buttonLink : `https://${a.buttonLink}`, '_blank', 'noopener,noreferrer')
            : undefined,
        }]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const items = [...notices, ...novedades];
  const unread = items.filter((i) => !read.includes(i.key));

  const markAllRead = () => {
    const next = Array.from(new Set([...read, ...items.map((i) => i.key)]));
    setRead(next);
    saveRead(customerId, next);
  };

  const toggle = () => {
    if (!open) {
      setNewOnOpen(unread.map((i) => i.key));
      markAllRead();
    }
    setOpen(!open);
  };

  // Cerrar con click afuera (escritorio) y con Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (window.innerWidth >= 640 && wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const renderItem = (item: BellItem) => {
    const Icon = item.kind === 'aviso' ? AlertTriangle : (NovedadIcon[item.icon || ''] || Megaphone);
    const isNew = newOnOpen.includes(item.key);
    return (
      <li key={item.key}>
        <button
          type="button"
          disabled={!item.onClick}
          onClick={() => { item.onClick?.(); setOpen(false); }}
          className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:hover:bg-transparent disabled:cursor-default transition-colors"
        >
          <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${item.kind === 'aviso' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
            <Icon size={18} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="font-semibold text-sm text-gray-900">{item.title}</span>
              {isNew && <span className="h-2 w-2 rounded-full bg-red-500" aria-label="nueva" />}
            </span>
            {item.description && <span className="block text-sm text-gray-600">{item.description}</span>}
            {item.onClick && (
              <span className="mt-1 inline-flex items-center text-xs font-semibold text-blue-700">
                {item.actionText || (item.kind === 'aviso' ? 'Resolver' : 'Ver más')} <ChevronRight size={14} />
              </span>
            )}
          </span>
        </button>
      </li>
    );
  };

  const avisos = items.filter((i) => i.kind === 'aviso');
  const nov = items.filter((i) => i.kind === 'novedad');

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggle}
        aria-label={unread.length ? `Notificaciones, ${unread.length} sin leer` : 'Notificaciones'}
        aria-expanded={open}
        className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-600 shadow-sm ring-1 ring-gray-200 hover:text-blue-700 hover:ring-blue-300 transition-colors"
      >
        <Bell size={22} />
        {unread.length > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white ring-2 ring-white">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="sm:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setOpen(false)} />
          <div
            role="dialog"
            aria-label="Notificaciones"
            className="fixed inset-x-0 bottom-0 top-auto z-50 max-h-[85dvh] flex flex-col rounded-t-2xl bg-white shadow-2xl sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-h-[70vh] sm:rounded-2xl sm:ring-1 sm:ring-gray-200"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <p className="font-semibold text-gray-900">Notificaciones</p>
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Cerrar" onClick={() => setOpen(false)} className="p-2 -mr-2 text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <Bell size={28} className="mx-auto text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">No tenés notificaciones nuevas.</p>
                </div>
              ) : (
                <>
                  {avisos.length > 0 && (
                    <>
                      <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">Avisos</p>
                      <ul className="divide-y divide-gray-100">{avisos.map(renderItem)}</ul>
                    </>
                  )}
                  {nov.length > 0 && (
                    <>
                      <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-blue-700">Novedades</p>
                      <ul className="divide-y divide-gray-100">{nov.map(renderItem)}</ul>
                    </>
                  )}
                </>
              )}
            </div>

            {onOpenPreferences && (
              <button
                type="button"
                onClick={() => { setOpen(false); onOpenPreferences(); }}
                className="flex items-center gap-3 border-t border-gray-100 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-left hover:bg-gray-50 sm:rounded-b-2xl"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
                  <Settings2 size={18} />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-gray-900">Preferencias de notificación</span>
                  <span className="block text-xs text-gray-500">Elegí qué te llega por WhatsApp y cada cuánto</span>
                </span>
                <ChevronRight size={18} className="text-gray-400" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
